// The sanctioned imperative fill for the Casper-compare (variant A/B) shipping
// estimators. Those `.shipping-estimator__shipping .l10n-*` spans are static
// template markup that React never owns the values of (unlike the estimator
// *blocks*, which render their own values from useShippingLocalization), so
// something has to write the location / cost / arrival date into them after the
// shipping API resolves. This decorator does exactly that, re-writing whenever
// the shared broker reports a new location.
//
// Two deliberate scoping choices:
//   - the `.shipping-estimator__shipping` prefix keeps this clear of the React-
//     owned estimator blocks (`.home-oct16-shipping-estimator__shipping`,
//     `.value-prop-list-block__shipping`), which can appear on the same compare
//     page and must not be double-written; and
//   - the presence guard skips the client-location fetch entirely on every
//     non-compare page (mirroring the legacy `if (!elems.length) return`).
import { format as formatDate } from "date-fns";
import { Key } from "ts-key-enum";
import { ILocationGuessResponse } from "tsi-common-react/src/models/location";
import { getDinero } from "tsi-common-react/src/utils/money";
import { t } from "ttag";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import shipping from "./shipping";

const MONEY_FORMAT = "$0,0.00";
const SCOPE = ".shipping-estimator__shipping";

interface EstimatorData {
    locationName: string;
    shippingCost: string;
    arrivalDate?: Date;
}

// These placeholders only ever hold plain text (a location name/zip, a
// formatted money string, a formatted date, and author-set plain-text
// prefixes), so textContent is both correct and safe -- the zip can be
// user-supplied, and textContent keeps it inert.
const fill = (document: Document, data: EstimatorData): void => {
    document
        .querySelectorAll<HTMLElement>(`${SCOPE} .l10n-fill-user-location`)
        .forEach((elem) => {
            if (data.locationName) {
                elem.textContent = data.locationName;
            }
        });
    document
        .querySelectorAll<HTMLElement>(`${SCOPE} .l10n-fill-shipping-cost`)
        .forEach((elem) => {
            const prefix = elem.dataset.prefix || "";
            const suffix = elem.dataset.suffix || "";
            if (data.shippingCost) {
                elem.textContent = `${prefix}${data.shippingCost}${suffix}`;
            }
        });
    document
        .querySelectorAll<HTMLElement>(
            `${SCOPE} .l10n-fill-shipping-arrival-date`,
        )
        .forEach((elem) => {
            const prefix = elem.dataset.prefix || "";
            const format = elem.dataset.format || "MMMM do";
            if (data.arrivalDate) {
                elem.textContent = `${prefix}${formatDate(data.arrivalDate, format)}`;
            }
        });
};

export const initCompareShippingLocalization = async (): Promise<void> => {
    const document = getDocument();
    if (!document) {
        return;
    }
    const hasSpans = document.querySelector(
        `${SCOPE} .l10n-fill-user-location, ${SCOPE} .l10n-fill-shipping-cost, ${SCOPE} .l10n-fill-shipping-arrival-date`,
    );
    if (!hasSpans) {
        return;
    }
    const apply = async (
        location: ILocationGuessResponse | null,
    ): Promise<void> => {
        if (!location?.zip) {
            return;
        }
        const response = await shipping.getShipmentArrivalDate(location.zip);
        if (!response) {
            return;
        }
        const cost = getDinero(response.shipping_cost);
        fill(document, {
            locationName: response.location_name || location.zip,
            shippingCost: cost.isZero() ? t`FREE` : cost.toFormat(MONEY_FORMAT),
            arrivalDate: response.arrives_on,
        });
    };
    shipping.on("change", (location: ILocationGuessResponse) => {
        void apply(location);
    });

    // Port of the legacy click-to-edit ZIP interaction (removed with the old
    // localization.ts). The compare page's `.l10n-fill-user-location.l10n-
    // editable` links swap to a ZIP input on click; committing a valid (>= 5
    // char) ZIP persists it via the shared broker and re-fills the estimator
    // (setClientLocation emits the `change` event apply() listens for). Without
    // this the four static estimator links have no way to initiate a location
    // change.
    const registerEditableLocationFields = (): void => {
        document
            .querySelectorAll<HTMLElement>(
                `${SCOPE} .l10n-fill-user-location.l10n-editable`,
            )
            .forEach((elem) => {
                elem.addEventListener("click", (event) => {
                    event.preventDefault();
                    const inputElem = document.createElement("input");
                    inputElem.classList.add("l10n-zip-input");
                    inputElem.placeholder = t`ZIP CODE`;
                    inputElem.setAttribute("id", "location-zip-code");
                    const update = async (): Promise<void> => {
                        // Disabling the input on commit synchronously fires
                        // `blur` on the now-disabled focused field, which would
                        // re-enter `update()`; the disabled flag short-circuits
                        // that second invocation.
                        if (inputElem.disabled) {
                            return;
                        }
                        const zip = `${inputElem.value}`;
                        if (zip.length < 5) {
                            return;
                        }
                        inputElem.disabled = true;
                        try {
                            await apply({ zip: zip });
                            shipping.setClientLocation(zip);
                            inputElem.remove();
                            elem.style.display = "block";
                        } catch {
                            inputElem.disabled = false;
                            inputElem.focus();
                        }
                    };
                    inputElem.addEventListener("blur", () => void update());
                    inputElem.addEventListener("keypress", (e) => {
                        if (e.key === Key.Enter) {
                            void update();
                        }
                    });
                    elem.style.display = "none";
                    if (elem.nextSibling) {
                        elem.parentNode?.insertBefore(
                            inputElem,
                            elem.nextSibling,
                        );
                    } else {
                        elem.parentNode?.appendChild(inputElem);
                    }
                    inputElem.focus();
                });
            });
    };
    registerEditableLocationFields();

    await apply(await shipping.getClientLocation());
};
